import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BookOpen, Columns3, Minus, Plus, Pencil, X } from "lucide-react";
import {
  subscribeWorkspace,
  subscribeSettings,
  saveKanbanBoard,
  toSortedArray,
} from "../../services/workspaceService";
import {
  normalizeKanbanBoard,
  addKanbanColumn,
  removeKanbanColumn,
  renameKanbanColumn,
  applyKanbanDrag,
  KANBAN_MAX_COLS,
  KANBAN_MIN_COLS,
} from "../../utils/kanbanBoard";
import { navigateToText } from "../../utils/textRoutes";
import { rememberWritingText } from "../../utils/writingSession";
import { READING } from "../../config/readingStates";
import { useEditLock } from "../../context/EditLockContext";
import HomeShell from "../shell/HomeShell";
import { BACKGROUNDS } from "../../config/backgrounds";
import deskGlass from "../../styles/deskGlass";

const glass = deskGlass;

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function cardId(textId) {
  return `kcard:${textId}`;
}
function colDropId(columnId) {
  return `kcol:${columnId}`;
}
function parseCardId(id) {
  const s = String(id || "");
  return s.startsWith("kcard:") ? s.slice(6) : null;
}
function parseColId(id) {
  const s = String(id || "");
  return s.startsWith("kcol:") ? s.slice(5) : null;
}

function collisionDetection(args) {
  // Prefer whatever is under the pointer (empty columns); fall back to corners.
  const hits = pointerWithin(args);
  if (hits.length) return hits;
  const rects = rectIntersection(args);
  if (rects.length) return rects;
  return closestCorners(args);
}

function findColumnOf(board, textId) {
  for (const col of board.columns) {
    if ((board.lists[col.id] || []).includes(textId)) return col.id;
  }
  return board.columns[0]?.id || null;
}

function KanbanCard({ text, onOpen }) {
  const id = cardId(text.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        ...glass.glassCard,
        padding: 0,
        overflow: "hidden",
        opacity: isDragging ? 0.35 : 1,
        border: "1px solid rgba(212,175,55,0.14)",
        userSelect: "none",
        WebkitUserSelect: "none",
        flexShrink: 0,
        minHeight: 0,
        height: "auto",
      }}
    >
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 10px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          touchAction: "none",
          userSelect: "none",
        }}
        title="Arrastrar a otra columna"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          style={{
            fontSize: "0.58rem",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            color: text.notebookColor || "#D4AF37",
            fontWeight: 700,
          }}
        >
          {text.notebookName}
        </span>
        <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem", letterSpacing: 1 }}>⋮⋮</span>
      </div>

      <div style={{ padding: "10px 12px", cursor: "pointer" }} onClick={() => onOpen(text)}>
        <h3 style={{ margin: 0, fontSize: "0.88rem", fontWeight: 800, color: "#fff", lineHeight: 1.25 }}>
          {text.title || "Sin título"}
        </h3>
        <p style={{ margin: "6px 0 0", fontSize: "0.72rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.35 }}>
          {stripHtml(text.content).slice(0, 120)}
        </p>
      </div>
      <div style={{ display: "flex", padding: "6px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(text);
          }}
          style={iconBtnGold}
          title="Abrir"
        >
          <BookOpen size={12} />
        </button>
      </div>
    </div>
  );
}

function KanbanColumn({ col, ids, textById, onOpen, renamingId, renameDraft, setRenameDraft, startRename, commitRename, locked, setRenamingId }) {
  const dropId = colDropId(col.id);
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  const sortableIds = ids.map(cardId);

  return (
    <div
      ref={setNodeRef}
      style={{
        minWidth: 260,
        maxWidth: 300,
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: isOver ? "rgba(212,175,55,0.12)" : "rgba(0,0,0,0.22)",
        border: isOver ? "1px solid rgba(212,175,55,0.4)" : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 16,
        padding: 12,
        maxHeight: "100%",
        minHeight: 320,
        width: 280,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 32, flexShrink: 0 }}>
        {renamingId === col.id ? (
          <input
            autoFocus
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setRenamingId(null);
                setRenameDraft("");
              }
            }}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(212,175,55,0.4)",
              color: "#fff",
              borderRadius: 8,
              padding: "6px 10px",
              fontSize: "0.85rem",
              fontWeight: 700,
              outline: "none",
            }}
          />
        ) : (
          <>
            <span
              style={{
                flex: 1,
                color: "#D4AF37",
                fontWeight: 800,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                fontSize: "0.78rem",
                cursor: locked ? "default" : "text",
              }}
              onDoubleClick={() => startRename(col)}
              title="Doble clic para renombrar"
            >
              {col.label}
            </span>
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem" }}>{ids.length}</span>
            {!locked && (
              <button type="button" onClick={() => startRename(col)} style={iconBtn} title="Renombrar">
                <Pencil size={12} />
              </button>
            )}
          </>
        )}
      </div>

      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            flex: 1,
            minHeight: 120,
            overflowY: "auto",
            borderRadius: 10,
            paddingBottom: 4,
            alignItems: "stretch",
          }}
        >
          {ids.map((tid) => {
            const t = textById[tid];
            if (!t) return null;
            return <KanbanCard key={tid} text={t} onOpen={onOpen} />;
          })}
        </div>
      </SortableContext>
    </div>
  );
}

function CardGhost({ text }) {
  if (!text) return null;
  return (
    <div
      style={{
        ...glass.glassCard,
        padding: "10px 12px",
        width: 260,
        border: "1px solid rgba(212,175,55,0.35)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        cursor: "grabbing",
      }}
    >
      <div style={{ fontSize: "0.58rem", color: text.notebookColor || "#D4AF37", fontWeight: 700, textTransform: "uppercase" }}>
        {text.notebookName}
      </div>
      <div style={{ marginTop: 4, fontWeight: 800, color: "#fff", fontSize: "0.88rem" }}>{text.title || "Sin título"}</div>
    </div>
  );
}

export default function Kanban() {
  const navigate = useNavigate();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [workspace, setWorkspace] = useState({ desks: [] });
  const [settings, setSettings] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [activeId, setActiveId] = useState(null);

  // distance: click body opens; drag only after intentional move on grip
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    const unsubW = subscribeWorkspace(setWorkspace);
    const unsubS = subscribeSettings(setSettings);
    return () => {
      unsubW();
      unsubS();
    };
  }, []);

  const allTexts = useMemo(() => {
    const texts = [];
    for (const desk of workspace?.desks || []) {
      for (const drawer of toSortedArray(desk.drawers)) {
        for (const notebook of toSortedArray(drawer.notebooks)) {
          for (const text of toSortedArray(notebook.texts)) {
            const s = text.readingState;
            if (s === READING.ARCHIVED || s === READING.TRASH) continue;
            texts.push({
              ...text,
              deskId: desk.id,
              deskSlug: desk.slug,
              drawerId: drawer.id,
              drawerSlug: drawer.slug,
              drawerName: drawer.name,
              notebookId: notebook.id,
              notebookSlug: notebook.slug,
              notebookName: notebook.name,
              notebookColor: notebook.color,
            });
          }
        }
      }
    }
    texts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    return texts;
  }, [workspace]);

  const textById = useMemo(() => {
    const map = {};
    for (const t of allTexts) map[t.id] = t;
    return map;
  }, [allTexts]);

  const board = useMemo(
    () => normalizeKanbanBoard(settings.kanbanBoard, allTexts.map((t) => t.id)),
    [settings.kanbanBoard, allTexts],
  );

  const persist = useCallback((next) => {
    saveKanbanBoard(next);
  }, []);

  const startRename = (col) => {
    if (locked) return;
    setRenamingId(col.id);
    setRenameDraft(col.label);
  };

  const commitRename = () => {
    if (!renamingId) return;
    persist(renameKanbanColumn(board, renamingId, renameDraft));
    setRenamingId(null);
    setRenameDraft("");
  };

  const handleOpen = (text) => {
    rememberWritingText(text);
    navigateToText(navigate, text);
  };

  const onDragStart = ({ active }) => {
    setActiveId(String(active.id));
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const textId = parseCardId(active.id);
    if (!textId) return;

    const sourceCol = findColumnOf(board, textId);
    if (!sourceCol) return;

    let destCol = parseColId(over.id);
    let destIndex = 0;
    const overCard = parseCardId(over.id);
    if (overCard) {
      destCol = findColumnOf(board, overCard);
      const list = board.lists[destCol] || [];
      destIndex = list.indexOf(overCard);
      if (destIndex < 0) destIndex = list.length;
    } else if (destCol) {
      destIndex = (board.lists[destCol] || []).length;
    } else {
      return;
    }

    const sourceIndex = (board.lists[sourceCol] || []).indexOf(textId);
    if (sourceIndex < 0) return;
    if (sourceCol === destCol && sourceIndex === destIndex) return;

    if (sourceCol === destCol) {
      const nextLists = {
        ...board.lists,
        [sourceCol]: arrayMove(board.lists[sourceCol] || [], sourceIndex, destIndex),
      };
      persist({ ...board, lists: nextLists });
      return;
    }

    persist(
      applyKanbanDrag(board, {
        sourceCol,
        destCol,
        sourceIndex,
        destIndex,
        textId,
      }),
    );
  };

  const activeText = activeId ? textById[parseCardId(activeId)] : null;

  return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
      <div
        style={{
          position: "relative",
          height: "calc(100vh - 64px)",
          overflow: "hidden",
          padding: 24,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap", flexShrink: 0 }}>
          <div>
            <h1 style={{ ...glass.greeting, margin: "0 0 6px" }}>Kanban</h1>
            <p style={{ ...glass.subtext, margin: 0 }}>
              {allTexts.length} notas · {board.columns.length} columnas ({KANBAN_MIN_COLS}–{KANBAN_MAX_COLS}) · arrastrá por ⋮⋮
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              disabled={locked || board.columns.length <= KANBAN_MIN_COLS}
              onClick={() => persist(removeKanbanColumn(board, board.columns[board.columns.length - 1].id))}
              style={{ ...glass.glassPill, opacity: locked || board.columns.length <= KANBAN_MIN_COLS ? 0.4 : 1 }}
              title="Quitar última columna"
            >
              <Minus size={14} /> Columna
            </button>
            <button
              type="button"
              disabled={locked || board.columns.length >= KANBAN_MAX_COLS}
              onClick={() => persist(addKanbanColumn(board, `Columna ${board.columns.length + 1}`))}
              style={{ ...glass.glassPill, opacity: locked || board.columns.length >= KANBAN_MAX_COLS ? 0.4 : 1 }}
              title="Añadir columna"
            >
              <Plus size={14} /> Columna
            </button>
            <button
              type="button"
              onClick={() => navigate("/corkboard")}
              style={{ ...glass.glassPill, display: "flex", alignItems: "center", gap: 6 }}
              title="Ir al corcho"
            >
              <Columns3 size={14} /> Corcho
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{ ...glass.glassActionBtn, width: 40, height: 40, borderRadius: "50%" }}
              title="Salir"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "stretch",
              overflowX: "auto",
              flex: 1,
              minHeight: 0,
              paddingBottom: 8,
            }}
          >
            {board.columns.map((col) => (
              <KanbanColumn
                key={col.id}
                col={col}
                ids={board.lists[col.id] || []}
                textById={textById}
                onOpen={handleOpen}
                renamingId={renamingId}
                renameDraft={renameDraft}
                setRenameDraft={setRenameDraft}
                startRename={startRename}
                commitRename={commitRename}
                locked={locked}
                setRenamingId={setRenamingId}
              />
            ))}
          </div>
          <DragOverlay dropAnimation={null}>
            <CardGhost text={activeText} />
          </DragOverlay>
        </DndContext>
      </div>
    </HomeShell>
  );
}

const iconBtn = {
  background: "transparent",
  border: "none",
  color: "rgba(255,255,255,0.4)",
  cursor: "pointer",
  padding: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
};

const iconBtnGold = { ...iconBtn, color: "#D4AF37" };
